import type { SiteSettings as SiteSettingsModel, SiteSettingsPayload, HrefTarget } from "../../types/layout";
import type {
	FooterData,
	FooterSocialIcon,
	HeaderData,
	LanguageOption
} from "@apa/core-renderers";
import { DEFAULT_LOGO_SRC, DEFAULT_LOGO_ALT, DEFAULT_LOGO_WHITE_SRC } from "@apa/core-renderers";

const SOCIAL_ICONS: FooterSocialIcon[] = ["linkedin", "youtube", "facebook", "twitter"];

export const PREVIEW_LOGO = DEFAULT_LOGO_SRC;
export const PREVIEW_FOOTER_LOGO = DEFAULT_LOGO_WHITE_SRC;
export const DEFAULT_LANG = "fr";

type HeaderSource = SiteSettingsPayload["header"] | SiteSettingsModel["header"];
type FooterSource = SiteSettingsPayload["footer"] | SiteSettingsModel["footer"];

const normalizeString = (value?: string | null) => (value ?? "").trim();

const normalizeHref = (href?: HrefTarget | null): HrefTarget | null => {
	const slug = normalizeString(href?.slug);
	const external = normalizeString(href?.external);
	const pageId = normalizeString(href?.pageId);
	if (!slug && !external && !pageId) return null;
	return {
		slug: slug || undefined,
		external: external || undefined,
		pageId: pageId || undefined
	};
};

type SanitizeOptions = {
	fallbackSlug?: string;
	requireHref?: boolean;
};

const sanitizeLink = (
	link?: { label?: string | null; href?: HrefTarget | null },
	options: SanitizeOptions = {}
) => {
	const label = normalizeString(link?.label);
	const href =
		normalizeHref(link?.href) ?? (options.fallbackSlug ? { slug: options.fallbackSlug } : undefined);

	if (!label) return null;
	if (options.requireHref && !href) return null;

	return href ? { label, href } : null;
};

export const buildHeaderData = (payload?: HeaderSource): HeaderData | null => {
	if (!payload) return null;
	const quickLinks = (payload.quickLinks ?? [])
		.map((link) => sanitizeLink(link, { fallbackSlug: "/", requireHref: true }))
		.filter((link): link is NonNullable<typeof link> => !!link);
	const nav = (payload.nav ?? [])
		.map((item) => ({
			label: normalizeString(item?.label),
			href: normalizeHref(item?.href),
			children: (item?.children ?? [])
				.map((child) => sanitizeLink(child, { fallbackSlug: "/", requireHref: true }))
				.filter((child): child is NonNullable<typeof child> => !!child)
		}))
		.filter((entry) => entry.label.length > 0 || entry.children.length > 0);

	if (!quickLinks.length && !nav.length) {
		return null;
	}

	return {
		logoAlt: normalizeString(payload.logoAlt) || DEFAULT_LOGO_ALT,
		quickLinksLabel: normalizeString(payload.quickLinksLabel),
		quickLinks,
		nav
	};
};

export const buildFooterData = (payload?: FooterSource): FooterData | null => {
	if (!payload) return null;
	const addresses = (payload.addresses ?? [])
		.map((address) => ({
			title: normalizeString(address?.title),
			lines: (address?.lines ?? []).map((line) => normalizeString(line)).filter((line) => line.length > 0),
			actions: (address?.actions ?? [])
				.map((action) => sanitizeLink(action, { fallbackSlug: "/", requireHref: true }))
				.filter((action): action is NonNullable<typeof action> => !!action)
		}))
		.filter((entry) => entry.title.length > 0 || entry.lines.length > 0);

	const groups = (payload.groups ?? [])
		.map((group) => ({
			title: normalizeString(group?.title),
			links: (group?.links ?? [])
				.map((link) => sanitizeLink(link, { fallbackSlug: "/", requireHref: true }))
				.filter((link): link is NonNullable<typeof link> => !!link)
		}))
		.filter((entry) => entry.title.length > 0 && entry.links.length > 0);

	const audiences = (payload.audiences ?? [])
		.map((link) => sanitizeLink(link, { fallbackSlug: "/", requireHref: true }))
		.filter((link): link is NonNullable<typeof link> => !!link);
	const legalLinks = (payload.legalLinks ?? [])
		.map((link) => sanitizeLink(link, { fallbackSlug: "/", requireHref: true }))
		.filter((link): link is NonNullable<typeof link> => !!link);
	const socials = (payload.socials ?? [])
		.map((social) => {
			const label = normalizeString(social?.label);
			const href = normalizeHref(social?.href);
			const icon = SOCIAL_ICONS.find((icon) => icon === social?.icon) ?? "linkedin";
			if (!label || !href) return null;
			return { label, href, icon };
		})
		.filter((entry): entry is NonNullable<typeof entry> => !!entry);

	if (!addresses.length && !groups.length && !audiences.length && !socials.length && !legalLinks.length) {
		return null;
	}

	return { addresses, groups, audiences, socials, legalLinks };
};

export const buildLanguageOptions = (locale: string): LanguageOption[] => {
	const normalized = (locale || DEFAULT_LANG).toLowerCase();
	const preferred = {
		code: normalized,
		label: normalized.toUpperCase()
	};
	const extras: LanguageOption[] = normalized === "en" ? [{ code: "fr", label: "FR" }] : [{ code: "en", label: "EN" }];
	return [preferred, ...extras];
};

export const BREAKPOINTS = {
	mobile: 768,
	medium: 1100
} as const;

export type DisplayViewportKey = "mobile" | "medium" | "desktop";

export const getDisplayViewportForWidth = (width?: number | null): DisplayViewportKey => {
	if (!width || width >= BREAKPOINTS.medium) {
		return "desktop";
	}
	if (width < BREAKPOINTS.mobile) {
		return "mobile";
	}
	return "medium";
};


